import { useEffect, useRef, useState } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, XAxis } from "recharts";
import { TrendingUp, TrendingDown, Activity, Cpu } from "lucide-react";

const PAIRS = ["BTC/USDT","ETH/USDT","SOL/USDT","BNB/USDT","XRP/USDT","ADA/USDT"];

function genSeed() {
  const arr = [];
  let v = 67420;
  for (let i = 0; i < 60; i++) {
    v += (Math.random() - 0.45) * 120;
    arr.push({ t: i, p: Math.round(v * 100) / 100 });
  }
  return arr;
}

export default function TradingTerminal({ active = true, extraTrades = [] }) {
  const [series, setSeries] = useState(genSeed);
  const [trades, setTrades] = useState(() => seedTrades());
  const [progress, setProgress] = useState(38);
  const counter = useRef(60);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      setSeries((s) => {
        const last = s[s.length - 1].p;
        const next = Math.max(50000, Math.round((last + (Math.random() - 0.48) * 250) * 100) / 100);
        counter.current += 1;
        return [...s.slice(1), { t: counter.current, p: next }];
      });
    }, 1200);
    return () => clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const pair = PAIRS[Math.floor(Math.random() * PAIRS.length)];
      const side = Math.random() > 0.5 ? "Buy" : "Sell";
      const price = (60000 + Math.random() * 12000).toFixed(2);
      const profit = (Math.random() * 280 - 30).toFixed(2);
      setTrades((t) => [{ id: Date.now() + Math.random(), pair, side, price, profit, time: "just now" }, ...t].slice(0, 12));
      setProgress((p) => Math.min(98, p + Math.random() * 1.2));
    }, 4500);
    return () => clearInterval(id);
  }, [active]);

  const last = series[series.length - 1].p;
  const prev = series[series.length - 2].p;
  const up = last >= prev;

  return (
    <div className="space-y-5" data-testid="trading-terminal">
      <div className="glass-strong rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Cpu className="w-5 h-5 text-[#00D68F]" />
              {active && <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#00D68F] rounded-full blink"></span>}
            </div>
            <div>
              <div className="font-semibold text-sm">Duo AI Trading Engine</div>
              <div className="text-[10px] uppercase tracking-widest text-[#00D68F]">{active ? "AI Bot Active" : "AI Bot Inactive"}</div>
            </div>
          </div>
          <div className={`text-right ${up ? "text-[#00D68F]" : "text-[#FF3B30]"}`}>
            <div className="font-mono text-2xl font-bold">${last.toLocaleString()}</div>
            <div className="text-xs flex items-center justify-end gap-1">
              {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              BTC/USDT
            </div>
          </div>
        </div>

        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <YAxis hide domain={["dataMin - 200", "dataMax + 200"]} />
              <XAxis hide dataKey="t" />
              <Tooltip
                contentStyle={{ background: "#0A0E27", border: "1px solid rgba(212,175,55,0.4)", borderRadius: 8 }}
                labelStyle={{ color: "#9BA1B9" }}
                itemStyle={{ color: "#D4AF37" }}
                formatter={(v) => [`$${v}`, "Price"]}
              />
              <Line type="monotone" dataKey="p" stroke="#00D68F" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="text-white/60 uppercase tracking-widest">Portfolio Growth</span>
            <span className="text-[#00D68F] font-bold">{progress.toFixed(1)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#00D68F] to-[#D4AF37] transition-all duration-500" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      </div>

      <div className="glass-strong rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-[#D4AF37]" />
          <div className="font-semibold text-sm">Live AI Trade Feed</div>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-hide" data-testid="trade-feed">
          {[...extraTrades, ...trades].map((tr, i) => (
            <div key={tr.id || `e${i}`} className="flex items-center justify-between text-xs font-mono px-3 py-2.5 rounded-md bg-white/5 hover:bg-white/8">
              <div className="flex items-center gap-3">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tr.side === "Buy" || tr.side === "buy" ? "bg-[#00D68F]/20 text-[#00D68F]" : "bg-[#FF3B30]/20 text-[#FF3B30]"}`}>{tr.side}</span>
                <span className="text-white">{tr.pair}</span>
                <span className="text-white/50">@ ${tr.price}</span>
              </div>
              <div className={`text-right ${parseFloat(tr.profit) >= 0 ? "text-[#00D68F]" : "text-[#FF3B30]"}`}>
                {parseFloat(tr.profit) >= 0 ? "+" : ""}${tr.profit}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function seedTrades() {
  const out = [];
  for (let i = 0; i < 8; i++) {
    out.push({
      id: i,
      pair: PAIRS[Math.floor(Math.random() * PAIRS.length)],
      side: Math.random() > 0.5 ? "Buy" : "Sell",
      price: (60000 + Math.random() * 12000).toFixed(2),
      profit: (Math.random() * 280 - 30).toFixed(2),
      time: `${i}m ago`,
    });
  }
  return out;
}
